import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { dropStaleWorkers, initI18n, initTheme } from '@mailserver/web-shared';
import { createAppRouter } from '@/router';
import './index.css';

/**
 * The admin panel's entry.
 *
 * **No service worker and no offline cache.** Both belonged to the mail client and were registered
 * here only because there was one bundle: an administrator who never opens a mailbox was given a
 * worker whose scope covered the whole console. A control panel is also the wrong thing to answer
 * from a cache — "what is the queue doing?" has no useful stale answer — so `networkMode` stays at
 * React Query's default rather than the webmail's `offlineFirst`.
 */
initTheme();
// This app registers no worker of its own, so any registration on this origin is the pre-split one
// that scoped the mail client's offline cache over the whole console. Drop it.
void dropStaleWorkers();
// Not awaited: the catalogue is a lazy import, and every string falls back to English until it
// lands. Blocking the first paint on a network round-trip to show the same words is a bad trade.
void initI18n();
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 10_000, refetchOnWindowFocus: false } },
});
const router = createAppRouter(queryClient);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
